import React, { useState } from 'react';
import {
  object,
  string,
  array,
  number,
  mixed,
} from 'yup';
import {
  Formik,
  Form,
} from 'formik';
import moment from 'moment';
import uniq from 'lodash/uniq';
import { useHistory, Link } from 'react-router-dom';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import { useTranslation } from 'react-i18next';

import useHttpStatus from '~/commons/hooks/useHttpStatus';
import {
  resolveTo,
  initFormValues,
} from '~/commons/helpers';

import FormHttpAlert from '~/commons/components/FormHttpAlert.jsx';
import FormHttpSaveBtn from '~/commons/components/FormHttpSaveBtn.jsx';
import BtnDel from '../btns/BtnDel.jsx';

import FieldsArticleGen from './FieldsArticleGen.jsx';
import FieldsArticleQtns from './FieldsArticleQtns.jsx';
import FormArticleAlertMsg from '../../../commons/components/FormArticleAlertMsg.jsx';

const schema = object({
  title: string().required(),
  subjects: array().of(
    string().required(),
  ),
  type: string().required(),
  extraReading: object().shape({
    url: string().url(),
  }),
  questions: array().of(object({
    question: string().required(),
    format: string().required(),
    score: mixed()
      .transform((value) => (value === '' ? undefined : Number(value)))
      .oneOf([10, 20, 30])
      .required('Score is required')
      .typeError('Score is required'),
    answer: mixed().when('format', {
      is: 'single-select',
      then: number().required().typeError('question requires an answer.'),
    }),
    answers: mixed()
      .when('format', {
        is: 'multi-select',
        then: array()
          .of(number())
          .min(1, 'question requires at least one answer.')
          .required(),
      })
      .when('format', {
        is: 'ordering',
        then: array()
          .of(number().required())
          .required('question requires an answer.')
          .test(
            'Duplicates',
            'question answer is duplicated.',
            (v) => uniq(v).length === v.length,
          ),
      })
      .when('format', {
        is: 'matching',
        then: array()
          .of(number().required())
          .required('question requires an answer.')
          .test(
            'Duplicates',
            'question answer is duplicated.',
            (v) => uniq(v).length === v.length,
          ),
      }),
  })),

});
const defaultQtn = {
  format: 'single-select',
  randomAnswers: true,
  question: '',
  choices: [''], // Start with one empty choice
  matches: [''], // Start with one empty match
  answer: null,
  answers: [],
  score: '10',
};

const defaultContent = {
  img: '',
  content: '',
  animation: null,
};

const defaults = {
  refCode: '',
  title: '',
  coverImg: null,
  tags: '',
  subjects: [],
  lv: '',
  extraReading: {
    title: '',
    coverImg: null,
    url: '',
    caption: '',
  },
  content: '',
  contents: [defaultContent],
  keyLearning: '',
  questions: [defaultQtn],
  type: '',
  pdfURL: '',
};

const fileUploadUrl = '/api/quiz/articles/files';

const FormArticle = ({
  article,
}) => {
  const history = useHistory();
  const [res, req] = useHttpStatus({
    onSuccess({ data }) {
      if (!article) {
        history.push(resolveTo(`../${data[0]._id}`));
      }
    },
  });
  const { t } = useTranslation();
  const [isSaveAs, setIsSaveAs] = useState(false);

  // Handle PDF download
  const handleDownloadPDF = () => {
    if (article && article._id) {
      window.open(`/api/quiz/answers/pdf/article/${article._id}`, '_blank');
    }
  };

  // Normalize choices and matches to object format for admin interface
  const normalizeData = (data) => {
    if (!data || !data.questions) return data;

    return {
      ...data,
      questions: data.questions.map((q) => ({
        ...q,
        choices: (q.choices || []).map((c) => (
          typeof c === 'string' ? { text: c, img: '' } : c
        )),
        matches: (q.matches || []).map((m) => (
          typeof m === 'string' ? { text: m, img: '' } : m
        )),
        // default score to '10' for old DB records that may not have score
        score: (() => {
          if (q.score == null) return '10';
          if (typeof q.score === 'number') return String(q.score);
          return q.score;
        })(),
      })),
    };
  };

  return (
    <Formik
      validationSchema={schema}
      initialValues={initFormValues(defaults, normalizeData(article), {
        omitIds: false,
      })}
      onSubmit={async (values, actions) => {
        const { status, payload } = await req({
          method: (article && !isSaveAs) ? 'patch' : 'post',
          url: `/api/quiz/articles${(article && !isSaveAs) ? `/${article._id}` : ''}`,
          data: {
            ...values,
            ...(isSaveAs && values.refCode ? { refCode: `${values.refCode}_${Date.now()}` } : {}), // eslint-disable-line no-nested-ternary
            tags: typeof values.tags === 'string'
              ? values.tags.split(',').map((s) => s.trim())
              : values.tags,
            questions: values.questions.map((q) => ({
              ...q,
              choices: (q.choices || []).filter((c) => {
                // Handle both string and object formats
                if (typeof c === 'string') {
                  return c.trim();
                }
                if (typeof c === 'object' && c !== null) {
                  // Keep choices that have either text or an image
                  const textOk = typeof c.text === 'string' && c.text.trim();
                  const imgOk = typeof c.img === 'string' && c.img.trim();
                  return textOk || imgOk;
                }
                return false;
              }),
              matches: (q.matches || []).filter((c) => {
                // Handle both string and object formats
                if (typeof c === 'string') {
                  return c.trim();
                }
                if (typeof c === 'object' && c !== null) {
                  // Keep matches that have either text or an image
                  const textOk = typeof c.text === 'string' && c.text.trim();
                  const imgOk = typeof c.img === 'string' && c.img.trim();
                  return textOk || imgOk;
                }
                return false;
              }),
              answers: (q.answers || []).filter((c) => c),
            })),
          },
        });
        if (status === 'success') {
          if (isSaveAs) {
            history.push(resolveTo(`../${payload[0]._id}`));
            return;
          }
          actions.setSubmitting(false);
          actions.resetForm({ values });
        }
      }}
    >
      <Form>
        <div className="form-row mb-3">
          <div className="col-auto">
            <FormHttpSaveBtn
              res={res}
              highlight
            />
          </div>
          {article && (
          <div className="col-auto">
            <FormHttpSaveBtn
              res={res}
              disabled={false}
              onClick={() => setIsSaveAs(true)}
            >{t('SaveAs')}</FormHttpSaveBtn>
          </div>
          )}
          {article && (
            <div className="col-auto">
              <Link
                to={resolveTo('./preview')}
                className="btn btn-primary"
              >
                <FA icon={faEye} />
                &nbsp;
                {t('Preview')}
              </Link>
            </div>
          )}
          {article && (
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleDownloadPDF}
              >
                <FA icon={faDownload} />
                &nbsp;
                {t('下載PDF')}
              </button>
            </div>
          )}
          {article && (
            <div className="col-auto mt-2">
              <span>
                {t('Last Modified')}: {
                  moment(article.updatedAt)
                    .format('DD/MM/YYYY h:mm a')
                }
              </span>
              &nbsp;
              <span>
                {(article.createdBy || article.updatedBy) && t('By')}
                &nbsp;
                {(article.updatedBy || article.createdBy)?.name?.full?.zhhk}
              </span>
            </div>
          )}
          <div className="col"></div>
          {article && (
            <div className="col-auto">
              <BtnDel
                api={{ url: `/api/quiz/articles/${article._id}` }}
                onDel={() => {
                  history.push(resolveTo('../'));
                }}
                theme={article.assignments.length ? 'danger' : undefined}
                message={article.assignments.length
                  ? `${t('DeleteArticle1')} ${t('Are you sure?')}`
                  : t('Are you sure you want to delete?')
                }
              />
            </div>
          )}
        </div>
        <FormHttpAlert res={res} />
        <FormArticleAlertMsg />

        <Tabs defaultActiveKey="gen" unmountOnExit>
          <Tab
            eventKey="gen"
            title={t('General')}
          >
            <div className="pt-3">
              <FieldsArticleGen fileUploadUrl={fileUploadUrl} defaultContent={defaultContent} />
            </div>
          </Tab>
          <Tab
            eventKey="qtns"
            title={t('Questions')}
          >
            <div className="py-3">
              <FieldsArticleQtns
                fileUploadUrl={fileUploadUrl}
                defaultQtn={defaultQtn}
                article={article}
              />
            </div>
          </Tab>
        </Tabs>

      </Form>
    </Formik>
  );
};

export default FormArticle;
